import { useContext, useMemo, useState } from "react";
import { bool, node } from "prop-types";
import { Tables } from "@repo/utils";
import { observer } from "mobx-react";
import { useEstimateStructure, useEstimateEditorStore, useEstimateComments } from "@tools";
import { EstimateEditorSettingsContext } from "./EstimateEditorSettingsContext";

export const useEstimateEditorSettings = () => {
  const context = useContext(EstimateEditorSettingsContext);
  if (context === null) {
    throw new Error("EstimateEditorSettingsContext cannot be null, please add a context provider");
  }
  return context;
};

export const EstimateEditorSettingsProvider = observer(({
  children,
  allowInteractions,
  hasEditorPrivileges,
  hasSellerPrivileges,
  readOnly,
  isProposal,
  isLibrary,
  isTemplate,
  noTurningOff,
  noCommenting,
  ...toPass
}) => {
  const structure = useEstimateStructure();
  const commentStore = useEstimateComments();
  const editorStore = useEstimateEditorStore();
  // const commentStore = useEstimateCommentStore();
  
  const [sectionsReordered, setSectionsReordered] = useState(false);
  
  
  const { settings } = structure;
  const { visibleTables } = settings;
  const { useExternalComments, useInternalComments } = commentStore;
  const {
    profitabilityMode,
    visibilityMode,
    currentVersionLocked,
    isArchived,
  } = editorStore;
  
  const onSectionReorder = () => {
    setSectionsReordered((state) => !state);
  };
  
  const isLocked = useMemo(() => (
    currentVersionLocked && !isProposal
  ), [currentVersionLocked, isProposal]);
  
  const breakdownTableIndex = useMemo(() => (
    visibleTables?.findIndex(t => t.name === Tables.BREAKDOWN)
  ), [visibleTables])
  
  const useProfitability = useMemo(() => (
    profitabilityMode && !readOnly && hasEditorPrivileges && hasSellerPrivileges && !isProposal
  ), [profitabilityMode, readOnly, hasEditorPrivileges, hasSellerPrivileges, isProposal]);
  
  const isSellerOrClient = useMemo(() => (
    isProposal || hasSellerPrivileges
  ), [ isProposal, hasSellerPrivileges ]);
  
  const isEditorOrClient = useMemo(() => (
    isProposal || hasEditorPrivileges
  ), [ isProposal, hasEditorPrivileges ]);
  
  const readOnlyMode = useMemo(() => (
    readOnly || isLocked
  ), [readOnly, isLocked]);
  
  const allowEdition = useMemo(() => (
    !readOnlyMode && hasEditorPrivileges && !visibilityMode
  ), [readOnlyMode, hasEditorPrivileges, visibilityMode]);
  
  const blockExpansions = useMemo(() => (
    readOnlyMode && !allowInteractions && !isLocked
  ), [readOnlyMode, allowInteractions, isLocked]);
  
  const allowCommenting = useMemo(() => (
    /*isEditorOrClient &&*/ !noCommenting && !readOnly && (isProposal || !isLocked)
  ), [noCommenting, isEditorOrClient, readOnly, isProposal, isLocked]);
  
  const showChatThread = useMemo(() => (
    allowInteractions && !visibilityMode && (useExternalComments || useInternalComments)
  ), [isEditorOrClient, allowInteractions, visibilityMode, useExternalComments, useInternalComments]);
  
  const value = {
    readOnly: readOnlyMode,
    isProposal,
    isLibrary,
    isTemplate,
    isLocked,
    hasEditorPrivileges,
    hasSellerPrivileges,
    noTurningOff,
    ...toPass,
    
    breakdownTableIndex,
    useProfitability,
    isSellerOrClient,
    isEditorOrClient,
    allowEdition,
    blockExpansions,
    allowInteractions,
    useVisibilityCell: allowInteractions && !noTurningOff && !readOnlyMode,
    forceAllowVersionSelect: isArchived,
    
    allowCommenting,
    showChatThread,
    
    sectionsReordered,
    onSectionReorder,
  }
  
  return <EstimateEditorSettingsContext.Provider value={value}>
    {children}
  </EstimateEditorSettingsContext.Provider>
})

EstimateEditorSettingsProvider.propTypes = {
  children: node.isRequired,
  allowInteractions: bool,
  hasEditorPrivileges: bool,
  hasSellerPrivileges: bool,
  readOnly: bool,
  isProposal: bool,
  isPdfPrinterView: bool,
  isPresetEditor: bool,
  isTemplate: bool,
  isLibrary: bool,
  showPdfCover: bool,
  noTurningOff: bool,
  noCommenting: bool,
  displayLibraryElementStatus: bool,
  allowLibraryElementStatusChange: bool,
  previewMode: bool,
}
